// components/FlashBar.jsx
import React, { useState, useEffect } from 'react';
import { X, Sparkles, Percent, CirclePercent, Dessert, CandyCane, Sparkle } from 'lucide-react';

const FlashBar = ({ config }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0 });

  const defaultConfig = {
    enabled: true,
    preset: 'clean-light',
    dismissible: true,
    devMode: false,
    messages: [{
      text: '25% off sitewide — Limited time',
      prefix: 'New Collection',
      ctaText: 'Shop Now',
      link: '/sale',
      countdownEnd: null,
    }],
    customStyles: {},
  };

  const finalConfig = { ...defaultConfig, ...config };
  const msg = finalConfig.messages?.[0] || {};
  const { devMode, customStyles = {} } = finalConfig;

  // ── Countdown timer (only for premium-dark) ───────────────────────
  useEffect(() => {
    if (finalConfig.preset !== 'premium-dark' || !msg.countdownEnd) return;

    const updateTimer = () => {
      const endTime = new Date(msg.countdownEnd).getTime();
      const now = Date.now();
      const diff = endTime - now;

      if (diff <= 0) {
        setTimeLeft({ hours: 0, minutes: 0 });
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setTimeLeft({ hours, minutes });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60_000);
    return () => clearInterval(interval);
  }, [finalConfig.preset, msg.countdownEnd]);

  // ── Dismissal persistence ─────────────────────────────────────────
  useEffect(() => {
    if (!finalConfig.enabled) return;

    const dismissed = localStorage.getItem('flashbar_dismissed');
    if (dismissed && finalConfig.dismissible) {
      try {
        const { until } = JSON.parse(dismissed);
        if (until > Date.now()) {
          setIsDismissed(true);
          return;
        }
      } catch {}
    }

    setIsVisible(true);
  }, [finalConfig.enabled, finalConfig.dismissible]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    if (finalConfig.dismissible) {
      const until = Date.now() + 24 * 60 * 60 * 1000; // 24h
      localStorage.setItem('flashbar_dismissed', JSON.stringify({ until }));
    }
  };

  if (!finalConfig.enabled || isDismissed || !isVisible || !msg.text) {
    return null;
  }

  // ── Style overrides ───────────────────────────────────────────────
  const containerStyle = {
    height: customStyles.height || (finalConfig.preset === 'clean-light' ? '56px' : '48px'),
    background: devMode && customStyles.backgroundColor ? customStyles.backgroundColor : undefined,
    color: devMode && customStyles.textColor ? customStyles.textColor : undefined,
  };

  const prefixStyle = {
    color: devMode && customStyles.prefixTextColor ? customStyles.prefixTextColor : undefined,
  };

  const iconStyle = {
    color: devMode && customStyles.prefixIconColor ? customStyles.prefixIconColor : undefined,
  };

  const ctaStyle = {
    color: devMode && customStyles.ctaColor ? customStyles.ctaColor : undefined,
  };

  // ── Icon mapping ──────────────────────────────────────────────────
  const ICON_MAP = {
    Sparkle,
    Sparkles,
    Percent,
    CirclePercent,
    Dessert,
    CandyCane,
  };

  const PrefixIconComponent = ICON_MAP[customStyles.prefixIcon] || null;

  // ── Render ────────────────────────────────────────────────────────
  if (finalConfig.preset === 'clean-light') {
    return (
      <div
        className="w-full transition-all duration-500 ease-out overflow-hidden"
        style={containerStyle}
        role="alert"
        aria-live="polite"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1 justify-center">
            {(PrefixIconComponent || msg.prefix) && (
              <div className="flex items-center gap-2">
                {PrefixIconComponent && (
                  <PrefixIconComponent className="w-4 h-4 flex-shrink-0" style={iconStyle} strokeWidth={2.2} />
                )}
                {msg.prefix && (
                  <span className="font-medium tracking-wide text-sm sm:text-base" style={prefixStyle}>
                    {msg.prefix}
                  </span>
                )}
              </div>
            )}

            {msg.prefix && <div className="hidden sm:block h-4 w-px bg-gray-300/70 mx-2" />}

            <span className="text-sm sm:text-base font-medium">
              {msg.text}
            </span>

            {msg.ctaText && msg.link && (
              <a
                href={msg.link}
                className="ml-3 text-sm sm:text-base font-semibold underline underline-offset-4 hover:opacity-90 transition"
                style={ctaStyle}
              >
                {msg.ctaText}
              </a>
            )}
          </div>

          {finalConfig.dismissible && (
            <button
              onClick={handleDismiss}
              className="p-2 -mr-2 hover:bg-black/5 rounded-full transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>
    );
  }

  if (finalConfig.preset === 'premium-dark') {
    const displayHours = String(timeLeft.hours).padStart(2, '0');
    const displayMinutes = String(timeLeft.minutes).padStart(2, '0');

    const hasCountdown = msg.countdownEnd && timeLeft.hours + timeLeft.minutes > 0;

    return (
      <div
        className="w-full bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white transition-all duration-500 ease-out overflow-hidden"
        style={containerStyle}
        role="alert"
        aria-live="polite"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1 justify-center">
            <div className="flex items-center gap-2.5">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
              {PrefixIconComponent && (
                <PrefixIconComponent className="w-4 h-4" style={iconStyle} strokeWidth={2.2} />
              )}
              {msg.prefix && (
                <span className="font-medium text-sm sm:text-base" style={prefixStyle}>
                  {msg.prefix}
                </span>
              )}
            </div>

            <span className="text-sm sm:text-base font-medium">{msg.text}</span>

            {hasCountdown && (
              <div className="flex items-center gap-2.5 ml-3">
                <span className="text-xs sm:text-sm opacity-90">Ends in</span>
                <div className="flex gap-1.5 font-mono">
                  <span className="text-xs sm:text-sm bg-white/10 px-2 py-0.5 rounded" style={ctaStyle}>
                    {displayHours}h
                  </span>
                  <span className="text-xs sm:text-sm bg-white/10 px-2 py-0.5 rounded" style={ctaStyle}>
                    {displayMinutes}m
                  </span>
                </div>
              </div>
            )}
          </div>

          {finalConfig.dismissible && (
            <button
              onClick={handleDismiss}
              className="p-2 -mr-2 hover:bg-white/10 rounded-full transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-300" strokeWidth={2.5} />
            </button>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default FlashBar;